<template>
  <v-dialog fullscreen value="true" persistent transition="dialog-bottom-transition">
    <v-card>
      <Toolbar
        title="Merge Users"
        color="systemPrimary"
        :hideClientDropdown="false"
        @close="$router.push({ name: 'people' })"
      />

      <div v-if="mergeState === null" class="pa-8">
        <Typography as="h4" variant="h-4" class="mb-6 text-left"
          >Combine duplicate accounts safely</Typography
        >

        <Alert
          v-if="isOne10PointsClient"
          variant="warning"
          message="User merge is not supported for One10 Points clients."
          class="mb-6"
        />

        <v-row align="center" class="mb-6" no-gutters>
          <v-col>
            <UserMergeCard
              label="User to Remove"
              :user="removeUser"
              :balance="removeUserParsed"
              @select="openDialog('remove')"
              @clear="clearUser('remove')"
            />
          </v-col>

          <v-col cols="auto" class="px-4">
            <v-icon size="40">mdi-arrow-right</v-icon>
          </v-col>

          <v-col>
            <UserMergeCard
              label="User to Keep"
              :user="keepUser"
              :balance="keepUserParsed"
              @select="openDialog('keep')"
              @clear="clearUser('keep')"
            />
          </v-col>
        </v-row>

        <MergeImpactSummary
          v-if="canMerge"
          :loading="balancesLoading"
          :impact="impact"
          class="mb-6"
        />

        <div v-if="canMerge" class="merge-info-box d-flex align-start mb-6" style="gap: 12px">
          <v-icon color="#2196f3" size="20" class="mt-1 flex-shrink-0"
            >mdi-information-outline</v-icon
          >
          <div>
            <Typography
              variant="body-1"
              class="text-left mb-1"
              style="color: #014361; font-weight: 500"
              >What will happen:</Typography
            >
            <Typography variant="body-2" class="text-left" style="color: #014361">
              The user's primary identifier will be:
              <strong>{{ keepUser.businessEmail || keepUser.businessPhone }}</strong>
            </Typography>
            <Typography variant="body-2" class="text-left" style="color: #014361"
              >All balances, recognitions and programs from both accounts will be merged</Typography
            >
          </div>
        </div>

        <v-checkbox
          v-if="canMerge"
          v-model="confirmed"
          label="I understand this will remove the account and cannot be undone"
          hide-details
          class="mt-0 mb-6 ml-2"
        />

        <v-divider class="mb-6" />

        <div class="d-flex justify-space-between">
          <Button text @click="$router.push({ name: 'people' })">Cancel</Button>
          <Button
            color="brandPrimary"
            class="white--text"
            :disabled="!canMerge || !confirmed"
            @click="startMerge"
            >Merge Users</Button
          >
        </div>
      </div>

      <div v-else-if="mergeState === 'progress'" class="d-flex justify-center align-center pa-8">
        <div style="width: 100%; max-width: 480px">
          <MergeProgress
            :promise="mergePromise"
            @done="mergeState = 'success'"
            @error="mergeState = 'error'"
          />
        </div>
      </div>

      <MergeSuccess v-else-if="mergeState === 'success'" @done="$router.push({ name: 'people' })" />

      <MergeError v-else-if="mergeState === 'error'" @retry="mergeState = null" />

      <UserSelectDialog
        v-model="dialog.show"
        :title="dialog.slot === 'remove' ? 'Select User to Remove' : 'Select User to Keep'"
        :excludeUserId="dialog.slot === 'remove' ? keepUser?.userId : removeUser?.userId"
        @select="handleUserSelect"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import Button from "@/components/generic/Button.vue";
import Toolbar from "@/components/generic/Toolbar.vue";
import Typography from "@/components/generic/Typography.vue";
import Alert from "@/components/generic/Alert.vue";
import MergeError from "@/components/merge-users/MergeError.vue";
import MergeImpactSummary from "@/components/merge-users/MergeImpactSummary.vue";
import MergeProgress from "@/components/merge-users/MergeProgress.vue";
import MergeSuccess from "@/components/merge-users/MergeSuccess.vue";
import UserMergeCard from "@/components/merge-users/UserMergeCard.vue";
import UserSelectDialog from "@/components/merge-users/UserSelectDialog.vue";
import UserServiceV3 from "@/services/UserServiceV3.js";
import WalletService from "@/services/WalletService.js";
import isOne10PointsEnabled from "@/mixins/one10Points/isOne10PointsEnabled";
import { mapState } from "vuex";

export default {
  name: "MergeUsers",
  title: "Merge Users",
  mixins: [isOne10PointsEnabled],
  components: {
    Alert,
    Button,
    MergeError,
    MergeImpactSummary,
    MergeProgress,
    MergeSuccess,
    Toolbar,
    Typography,
    UserMergeCard,
    UserSelectDialog,
  },
  data() {
    return {
      removeUser: null,
      keepUser: null,
      removeUserBalance: null,
      keepUserBalance: null,
      balancesLoading: false,
      confirmed: false,
      mergeState: null,
      mergePromise: null,
      dialog: {
        show: false,
        slot: null,
      },
    };
  },
  computed: {
    ...mapState(["globalClientId"]),
    canMerge() {
      return !!this.removeUser && !!this.keepUser;
    },
    isOne10PointsClient() {
      return this.isOne10PointsEnabled(this.globalClientId);
    },
    removeUserParsed() {
      return this.removeUserBalance ? this.parseBalance(this.removeUserBalance) : null;
    },
    keepUserParsed() {
      return this.keepUserBalance ? this.parseBalance(this.keepUserBalance) : null;
    },
    impact() {
      if (!this.removeUserParsed || !this.keepUserParsed) return null;
      return { removeUser: this.removeUserParsed, keepUser: this.keepUserParsed };
    },
  },
  watch: {
    globalClientId() {
      this.removeUser = null;
      this.keepUser = null;
      this.removeUserBalance = null;
      this.keepUserBalance = null;
      this.confirmed = false;
    },
  },
  methods: {
    openDialog(slot) {
      this.dialog.slot = slot;
      this.dialog.show = true;
    },
    clearUser(slot) {
      if (slot === "remove") {
        this.removeUser = null;
        this.removeUserBalance = null;
      } else {
        this.keepUser = null;
        this.keepUserBalance = null;
      }
      this.confirmed = false;
    },
    handleUserSelect(user) {
      const slot = this.dialog.slot;
      if (slot === "remove") {
        this.removeUser = user;
        this.removeUserBalance = null;
      } else {
        this.keepUser = user;
        this.keepUserBalance = null;
      }
      this.fetchUserBalance(user.userId, slot);
    },
    async fetchUserBalance(userId, slot) {
      this.balancesLoading = true;
      try {
        const balances = await WalletService.checkUserBalanceV2(userId);
        if (slot === "remove") this.removeUserBalance = balances;
        else this.keepUserBalance = balances;
      } catch (err) {
        console.log("Error fetching user balance", err);
      } finally {
        this.balancesLoading = false;
      }
    },
    startMerge() {
      this.mergePromise = UserServiceV3.mergeUsers(this.removeUser.userId, this.keepUser.userId);
      this.mergeState = "progress";
    },
    parseBalance(balances) {
      const cash = balances.find((b) => b.awardId == 1 && b.type === "DEFAULT");
      const otherMap = {};
      balances
        .filter((b) => b.awardId != 1)
        .forEach((b) => {
          if (!otherMap[b.awardId]) {
            otherMap[b.awardId] = { ...b, balance: 0 };
          }
          otherMap[b.awardId].balance += b.balance;
        });
      return {
        cashBalance: cash?.balance || 0,
        otherBalances: Object.values(otherMap),
      };
    },
  },
};
</script>

<style scoped>
.merge-info-box {
  background-color: #e5f6fd;
  border: 1px solid #2196f3;
  border-radius: 8px;
  padding: 6px 16px 6px 12px;
}
</style>
